import { Box, Button, Grid, TextField, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../../../components/CustomButton";
import CustomModal from "../../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../../components/CustomTextField";
import CustomSelect from "../../../../../components/Select/Select";
import {
  getPetsCompliantSummaryWoLoader,
  updateProcedureOnlyById,
} from "../../../../../redux/reducers/petSlice";
import { AppColors } from "../../../../../util/AppColors";

const defObj = {
  clinic: {},
  vets: [],
  services: [],
  stocks: [],
  facilities: [],
  locations: [],
  serviceRemainders: [],
};
const initVal = { name: null, variant: null, quantity: null };
const reqTxt = "This field is required";

const getRemainderId = (tmp) =>
  tmp?.serviceRemainderId ?? tmp?.templateId ?? tmp?.id ?? null;

const getTemplateLinkedIds = (tmp) =>
  (tmp?.linkedServices ?? tmp?.serviceIds ?? [])
    ?.map((ser) =>
      typeof ser === "object" && ser !== null
        ? (ser?.serviceId ?? ser?.value)
        : ser,
    )
    ?.filter((id) => id != null && id !== "");

const templateLinksService = (tmp, serviceId) => {
  if (serviceId == null || serviceId === "") return false;
  return getTemplateLinkedIds(tmp)?.some(
    (id) => String(id) === String(serviceId),
  );
};

const getOccurrenceLabel = (remainders, serviceId) => {
  if (serviceId == null || serviceId === "") return "Dose";
  const tmp = (Array.isArray(remainders) ? remainders : []).find((t) =>
    templateLinksService(t, serviceId),
  );
  return tmp?.occurrenceLabel?.trim() || "Dose";
};

const EditProcedureDrawer = ({ open, onClose, procedure }) => {
  const dispatch = useDispatch();
  const [values, setValues] = useState(initVal);
  const reqDet = useSelector((state) => state?.pet?.complaintSummary?.data);
  const appDet = reqDet?.appointment;
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const {
    clinic: clinicDet,
    vets: clinicVets,
    services: clinicServices,
    stocks: clinicStockList,
    facilities,
    locations,
    serviceRemainders,
  } = useMemo(() => {
    if (!mandateDataRaw) return defObj;
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        clinic: reqData?.clinic ?? {},
        vets: Array.isArray(reqData?.vets) ? reqData?.vets : [],
        services: Array.isArray(reqData?.services) ? reqData?.services : [],
        stocks: Array.isArray(reqData?.stocks) ? reqData?.stocks : [],
        facilities: Array.isArray(reqData?.facilities)
          ? reqData?.facilities
          : [],
        locations: Array.isArray(reqData?.locations) ? reqData?.locations : [],
        serviceRemainders: Array.isArray(reqData?.serviceRemainders)
          ? reqData.serviceRemainders
          : [],
      };
    } catch {
      return defObj;
    }
  }, [mandateDataRaw]);
  const services = clinicServices;

  useEffect(() => {
    if (procedure) {
      const reqService = getServiceList()?.find(
        (ser) => ser?.serviceId === procedure?.serviceId,
      );
      const reqVariant = getVarinatList(procedure?.serviceId)?.find(
        (vl) => vl?.variantId === procedure?.procedureVariantId,
      );
      const reqDose = getDoseInSeriesList(procedure?.serviceId)?.find(
        (d) =>
          procedure?.doseInSeries != null &&
          String(d?.stepId) === String(procedure?.doseInSeries) &&
          (procedure?.serviceRemainderId == null ||
            String(d?.serviceRemainderId) ===
              String(procedure?.serviceRemainderId)),
      );
      setValues({
        service: reqService,
        serviceId: procedure?.serviceId,
        variant: reqVariant,
        quantity: procedure?.quantity,
        doseInSeries: reqDose ?? null,
      });
    }
  }, [procedure]);

  const getServiceList = () => {
    const reqServList = services
      ?.filter(
        (sl) =>
          sl?.active === "Y" &&
          !["lab", "radiology"].includes(sl?.category?.toLowerCase()),
      )
      ?.map((ser) => ({
        name: ser?.serviceName,
        value: ser?.serviceName,
        serviceId: ser?.serviceId,
        category: ser?.category,
        subCategory: ser?.subCategory,
        remarks: ser?.description,
      }));
    return reqServList;
  };

  const onApiCall = () => {
    dispatch(getPetsCompliantSummaryWoLoader(appDet?.appointmentId));
    onClose();
  };

  const handleSave = () => {
    const serviceId = values?.serviceId ?? values?.service?.serviceId;
    const doseRequired = getDoseInSeriesList(serviceId).length > 0;
    if (
      !serviceId ||
      !(values?.variantId ?? values?.variant?.variantId) ||
      !values?.quantity ||
      values?.quantity === 0 ||
      (doseRequired && !values?.doseInSeries?.stepId)
    ) {
      const reqObj = { ...values };
      if (!serviceId) {
        reqObj.servErr = true;
      }
      if (!(values?.variantId ?? values?.variant?.variantId)) {
        reqObj.varErr = true;
      }
      if (!values?.quantity || Number(values?.quantity) === 0) {
        reqObj.qtyErr = true;
      }
      if (doseRequired && !values?.doseInSeries?.stepId) {
        reqObj.doseErr = true;
      }
      return setValues(reqObj);
    }
    const reqQty = Number(values?.quantity) > 0 ? Number(values?.quantity) : 1;
    const resServ = services?.find(
      (ser) => ser?.serviceId === values?.service?.serviceId,
    );
    const reqVar = resServ?.serviceVariants?.find(
      (vnt) => vnt?.variantName === values?.variant?.name,
    );
    let tax = 0;
    let total = reqVar?.servicePrice ?? 0;
    if (resServ?.taxApplicability?.toLowerCase() === "yes") {
      tax +=
        resServ?.taxApplicability?.toLowerCase() === "yes"
          ? (Number(reqVar?.servicePrice ?? 0) / 100) *
            Number(resServ?.tax ?? 0)
          : 0;
      total += tax;
    }
    const data = {
      procedureName: values?.service?.name,
      serviceId: values?.service?.serviceId,
      category: values?.service?.category,
      subCategory: values?.service?.subCategory,
      serviceCharge: reqVar?.servicePrice ?? 0,
      serviceType: values?.variant?.name,
      tax: Number(Number(tax) * Number(reqQty)),
      total: Number(Number(total) * Number(reqQty)),
      mrpPrice: Number(reqVar?.servicePrice ?? 0),
      procedureVariantId: values?.variant?.variantId,
      quantity: Number(reqQty),
      patientWorkbook: true,
      mainProcedure: true,
      hsnSacCode: resServ?.hsnSacCode ?? null,
      paymentId: reqDet?.paymentId ?? null,
      ...(values?.doseInSeries?.stepId
        ? {
            doseInSeries: values?.doseInSeries?.stepId,
            serviceRemainderId:
              values?.doseInSeries?.serviceRemainderId ?? null,
          }
        : {}),
    };
    dispatch(
      updateProcedureOnlyById({ id: procedure?.procedureId, data }),
    ).then((res) => {
      if (res?.payload) onApiCall();
    });
  };

  const getVarinatList = (serId, serviceSource = null) => {
    if (serId == null || serId === "") return [];
    const src = Array.isArray(serviceSource)
      ? serviceSource
      : Array.isArray(services)
        ? services
        : [];
    const variants = src
      ?.find((ser) => ser?.serviceId === serId)
      ?.serviceVariants?.filter(
        (vr) => vr?.createdBy === clinicDet?.id && vr?.approved,
      );
    if (!Array.isArray(variants)) return [];
    return variants.map((vnt) => ({
      name: vnt?.variantName,
      variantId: vnt?.variantId,
      servicePrice: vnt?.servicePrice ?? 0,
      value: vnt?.variantName,
      serviceCharge: vnt?.serviceCharge,
      variantNumber: vnt?.variantNumber,
      serviceId: serId,
    }));
  };

  const getDoseInSeriesList = (serId) => {
    if (serId == null || serId === "") return [];
    const remainders = Array.isArray(serviceRemainders)
      ? serviceRemainders
      : [];
    const linkedTemps = remainders.filter((tmp) =>
      templateLinksService(tmp, serId),
    );
    const prefixTemplate = linkedTemps.length > 1;
    const options = [];
    linkedTemps.forEach((tmp) => {
      const steps = tmp?.steps ?? tmp?.serviceRemainderSteps ?? [];
      if (!Array.isArray(steps) || steps.length === 0) return;
      const occurrence = tmp?.occurrenceLabel?.trim() || "Dose";
      const remainderId = getRemainderId(tmp);
      const templateName = tmp?.templateName ?? tmp?.name ?? "";
      steps.forEach((st, index) => {
        const stepName =
          st?.stepName ?? st?.name ?? `${occurrence} ${index + 1}`;
        const stepId = st?.stepId ?? st?.id ?? `${remainderId}-${index + 1}`;
        const displayName =
          prefixTemplate && templateName
            ? `${templateName} · ${stepName}`
            : stepName;
        options.push({
          name: displayName,
          value: displayName,
          stepId,
          stepName,
          stepOrder: st?.stepOrder ?? index + 1,
          serviceRemainderId: remainderId,
          templateName,
        });
      });
    });
    return options;
  };

  const onChange = (name, value) => {
    const reqObj = { ...values, [name]: value };
    if (name === "variant") {
      reqObj.variantId = value?.variantId;
      reqObj.varErr = value?.variantId ? false : true;
    }
    if (name === "quantity") {
      reqObj.qtyErr = Number(value) > 0 ? false : true;
    }
    if (name === "doseInSeries") {
      reqObj.doseErr = !value?.stepId;
    }
    setValues(reqObj);
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      header={<>Edit procedure — {procedure?.procedureName}</>}
      modal
      modalWidth={40}
      reduceHeaderSize
    >
      <Box
        display="flex"
        flexDirection="column"
        sx={{ height: "calc(100vh - 75px)" }}
      >
        {/* Drawer Body */}
        <Box p={3} flexGrow={1} overflow="auto">
          <Box mb={3}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#616161",
                letterSpacing: "0.5px",
                mb: 1,
                textTransform: "uppercase",
              }}
            >
              VARIANT
            </Typography>
            <CustomSelect
              list={getVarinatList(values?.service?.serviceId)}
              value={values?.variant?.name}
              handleChange={(e) => {
                onChange(
                  "variant",
                  getVarinatList(values?.service?.serviceId)?.find(
                    (li) => li?.name === e?.target?.value,
                  ),
                );
              }}
              error={values?.varErr}
              helperText={values?.varErr ? "Please select a variant" : ""}
              select
            />
            <Typography
              sx={{
                fontSize: "12px",
                color: "#757575",
                mt: 1,
                lineHeight: 1.4,
              }}
            >
              Selecting a procedure with a default kit makes it available under
              "Add from procedure kit" below.
            </Typography>
          </Box>
          <Box mb={3}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#616161",
                letterSpacing: "0.5px",
                mb: 1,
                textTransform: "uppercase",
              }}
            >
              BILLABLE QTY
            </Typography>
            <CustomTextField
              value={values?.quantity}
              handleChange={(e) => {
                onChange("quantity", e?.target?.value);
              }}
              fullWidth
              error={values?.qtyErr}
              helperText={values?.qtyErr ? reqTxt : ""}
              type="number"
            />
          </Box>

          {getDoseInSeriesList(values?.service?.serviceId).length > 0 ? (
            <Box mb={3}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: values?.doseErr ? "#d32f2f" : "#616161",
                  letterSpacing: "0.5px",
                  mb: 1,
                  textTransform: "uppercase",
                }}
              >
                {getOccurrenceLabel(
                  serviceRemainders,
                  values?.service?.serviceId,
                )}{" "}
                IN SERIES <span style={{ color: "#d32f2f" }}>*</span>
              </Typography>
              <CustomSelect
                list={getDoseInSeriesList(values?.service?.serviceId)}
                value={values?.doseInSeries?.name}
                handleChange={(e) => {
                  onChange(
                    "doseInSeries",
                    getDoseInSeriesList(values?.service?.serviceId)?.find(
                      (li) => li?.name === e?.target?.value,
                    ) ?? null,
                  );
                }}
                select
                error={values?.doseErr}
                helperText={values?.doseErr ? reqTxt : ""}
              />
            </Box>
          ) : null}

          <Grid container spacing={2} mb={3}>
            <Grid item xs={6}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#616161",
                  letterSpacing: "0.5px",
                  mb: 1,
                  textTransform: "uppercase",
                }}
              >
                SERVICE CHARGE (₹)
              </Typography>
              <TextField
                type="number"
                fullWidth
                size="small"
                value={procedure?.serviceCharge}
                sx={{
                  backgroundColor: "#fff",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                  },
                }}
                disabled
              />
            </Grid>
            <Grid item xs={6}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#616161",
                  letterSpacing: "0.5px",
                  mb: 1,
                  textTransform: "uppercase",
                }}
              >
                CONSUMABLES CHARGE (₹)
              </Typography>
              <TextField
                type="number"
                fullWidth
                size="small"
                value={procedure?.serviceCharge}
                sx={{
                  backgroundColor: "#fff",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                  },
                }}
                disabled
              />
            </Grid>
          </Grid>

          <Box mb={3}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#616161",
                letterSpacing: "0.5px",
                mb: 1,
                textTransform: "uppercase",
              }}
            >
              PACKAGE PRICE{" "}
              <span style={{ fontWeight: 400, color: "#757575" }}>
                (Service + Consumables — not entered directly)
              </span>
            </Typography>
            <Box
              sx={{
                backgroundColor: "#f5f7fa",
                border: "1px solid #e0e0e0",
                borderRadius: "6px",
                p: 1.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 700,
                  color: AppColors.appPrimary || "#0054A6",
                }}
              >
                ₹{Number(procedure?.serviceCharge)?.toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Drawer Footer */}
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={1.5}
          px={3}
          py={2}
          borderTop="1px solid #e0e0e0"
          backgroundColor="#fafafa"
        >
          <Button
            onClick={onClose}
            sx={{
              color: "#616161",
              textTransform: "none",
              fontWeight: 600,
              px: 2,
            }}
          >
            Cancel
          </Button>
          <CustomButton text="Save changes" smallBtn onClick={handleSave} />
        </Box>
      </Box>
    </CustomModal>
  );
};

export default EditProcedureDrawer;
